import { fetchClient } from '@/api/typedClient'
import type { components } from '@/types/api/v1'
import { message } from 'antd'

type CrossroadsRoutingInfo = components['schemas']['CrossroadsRoutingInfo']

export interface RetryBatchResult {
  success: boolean
}

/**
 * Retries a supply plan batch's failed log via POST /supply_plan/retry_batch
 * — resets every non-synced plan in the batch back to origin_mapped and
 * re-publishes the upsert saga.
 *
 * X-Tenant-Name/X-Dest-Tenant-Name/X-Origin-Instance-Name are set explicitly
 * from the batch's own routing (captured when it was first received), not
 * left to the client's tenant-header middleware default — the currently
 * selected UI tenant could be either side of the batch (sent or received),
 * but CrossroadsSupplyPlan.by_batch matches on the exact origin/destination
 * tenant+instance IDs, so the headers must reflect the batch's actual
 * routing regardless of which tenant is browsing.
 */
export async function retrySupplyPlanBatch(
  routing: CrossroadsRoutingInfo,
  batchId: string,
): Promise<RetryBatchResult> {
  message.loading({
    content: 'Retrying batch...',
    key: 'retry-batch',
    duration: 0,
  })

  const { data, error } = await fetchClient.POST(
    '/crossroads/supply_plan/retry_batch',
    {
      params: {
        query: { batch_id: batchId },
        header: {
          'X-Tenant-Name': routing.origin_tenant_name,
          'X-Dest-Tenant-Name': routing.destination_tenant_name,
          'X-Origin-Instance-Name': routing.origin_instance_name,
        },
      },
    },
  )

  if (error) {
    const detail =
      typeof error.detail === 'string' ? error.detail : 'Unknown error'
    message.error({ content: `Failed to retry: ${detail}`, key: 'retry-batch' })
    return { success: false }
  }

  if (data.status === 'pending') {
    message.warning({ content: data.message, key: 'retry-batch' })
    return { success: false }
  }

  message.success({ content: data.message, key: 'retry-batch' })
  return { success: true }
}
